import { Card, Container, Table, TableBody, TableContainer, TableHead, TableRow, Chip, Typography } from "@mui/material";
import axios, { endpoints } from "src/utils/axios";
import { memo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { DisplayPageData } from "src/components/display-page-data";
import NoneFoundReport from "src/components/none-found-report";
import { useSettingsContext } from "src/components/settings";
import PageHeader from "src/components/page-header";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import useModelData from "src/hooks/use-model-data";
import { EldVehicleDTCHistory, DtcCodeParsedHistory } from "src/models";
import { formatDatetimeForPreview, addDaysToDate, today } from "src/utils/date-utils";
import { useSort } from "src/hooks/use-sort";
import { enqueueSnackbar } from "notistack";
import { useAuthContext } from "src/auth/hooks";
import { SearchBox } from "src/components/search-card";
import { useSearch } from "src/hooks/use-search";
import { ReportRunner, useDateRangeFilter } from "../../reports/runner/report-runner";

const getDescription = (parsed: DtcCodeParsedHistory): string => {
  if (parsed.format === 'OBD-II') return `${parsed.code}: ${parsed.description}`;
  if (parsed.format === 'J1939') return `SPN ${parsed.spn}: ${parsed.spnDesc}`;
  if (parsed.format === 'J1708') return `PID ${parsed.pid}: ${parsed.pidDesc}`;
  return '-';
};

const getFaultMode = (parsed: DtcCodeParsedHistory): string => {
  if (parsed.format === 'OBD-II') return '-';
  if (parsed.format === 'J1939') return `FMI ${parsed.fmi}: ${parsed.fmiDesc}`;
  if (parsed.format === 'J1708') return `FMI ${parsed.fmi}: ${parsed.fmiDesc}`;
  return '-';
};

const getOccurrence = (parsed: DtcCodeParsedHistory): number => {
  if (parsed.format === 'OBD-II') return 1;
  return parsed.occurrence || 1;
};

type EldMaintenanceListProps = {
  dtcHistory: EldVehicleDTCHistory[];
  initialSearch?: string;
}

const EldMaintenanceList = memo(({ dtcHistory, initialSearch }: EldMaintenanceListProps) => {
  const { sortedList, sortData } = useSort(dtcHistory);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, [
    "vin",
    "vehicleUnitNumber"
  ], initialSearch);

  return (
    <div>
      <Card>
        <SearchBox searchText={searchText} setSearchText={setSearchText} searchingFor="Diagnostic Codes" mb={0} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeader sortKey="vehicleUnitNumber" sortData={sortData} align="left">
                  Vehicle
                </SortableHeader>
                <SortableHeader sortKey="vin" sortData={sortData} align="left">
                  VIN
                </SortableHeader>
                <TruxTableCell align="left">
                  Fault Description
                </TruxTableCell>
                <TruxTableCell align="left">
                  Fault Mode
                </TruxTableCell>
                <TruxTableCell align="center">
                  Occurrence
                </TruxTableCell>
                <SortableHeader sortKey="currentStatus" sortData={sortData} align="center">
                  Status
                </SortableHeader>
                <SortableHeader sortKey="lastOnAt" sortData={sortData} align="center">
                  Last On
                </SortableHeader>
                <SortableHeader sortKey="lastOffAt" sortData={sortData} align="center">
                  Last Off
                </SortableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredList.map((dtc, index) => {
                const parsed = dtc.dtcCodesParsed;
                const occurrence = parsed ? getOccurrence(parsed) : 1;

                return (
                  <TableRow key={index} hover>
                    <TruxTableCell align="left">
                      <Typography variant="body2" fontWeight={600}>
                        {dtc.vehicleUnitNumber}
                      </Typography>
                    </TruxTableCell>
                    <TruxTableCell align="left">
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "11px" }}>
                        {dtc.vin}
                      </Typography>
                    </TruxTableCell>
                    <TruxTableCell align="left">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", maxWidth: 220 }}>
                        {parsed ? getDescription(parsed) : "-"}
                      </Typography>
                    </TruxTableCell>
                    <TruxTableCell align="left">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px", maxWidth: 220 }}>
                        {parsed ? getFaultMode(parsed) : "-"}
                      </Typography>
                    </TruxTableCell>
                    <TruxTableCell align="center">
                      {occurrence > 1 ? (
                        <Typography variant="body2" sx={{ color: "#e65100", fontWeight: 500 }}>
                          × {occurrence}
                        </Typography>
                      ) : (
                        <Typography variant="body2">1</Typography>
                      )}
                    </TruxTableCell>
                    <TruxTableCell align="center">
                      <Chip
                        label={dtc.currentStatus}
                        color={dtc.currentStatus === "ON" ? "error" : "success"}
                        size="small"
                        sx={{ fontSize: "10px", height: "20px", fontWeight: 600 }}
                      />
                    </TruxTableCell>
                    <TruxTableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: "11px" }}>
                        {dtc.lastOnAt && dtc.lastOnAt !== "1970-01-01T00:00:00Z"
                          ? formatDatetimeForPreview(dtc.lastOnAt)
                          : "-"
                        }
                      </Typography>
                    </TruxTableCell>
                    <TruxTableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: "11px" }}>
                        {dtc.lastOffAt && dtc.lastOffAt !== "1970-01-01T00:00:00Z"
                          ? formatDatetimeForPreview(dtc.lastOffAt)
                          : "-"
                        }
                      </Typography>
                    </TruxTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </div>
  );
});

type PageModel = {
  dtcHistory?: EldVehicleDTCHistory[];
}

export default function Page() {
  const settings = useSettingsContext();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";

  const currentRequest = useRef({} as any);

  const dataLoader = useCallback(() => ({
    dtcHistory: (currentRequest.current as any).ran && axios.post(
      endpoints.eldVehicleDTCHistory.findAll,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data || []) || [],
  }), []);

  const {
    data,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const dateRangeFilter = useDateRangeFilter({
    fromDate: addDaysToDate(today(timezone || "America/Chicago"), -30),
    toDate: today(timezone || "America/Chicago"),
  });

  const filters = [
    dateRangeFilter
  ];

  const runOnFirstRenderRef = useRef(false);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Diagnostic Codes" />

        <ReportRunner
          runOnFirstRender={runOnFirstRenderRef}
          filters={filters}
          onValidate={(errorMessage) => enqueueSnackbar(errorMessage, {variant: "error"})}
          onRun={(request) => {
            currentRequest.current = request;
            setShouldRefresh(true);
          }}
          loading={loading}
        />

        {(data?.dtcHistory?.length || 0) > 0 && (
          <EldMaintenanceList dtcHistory={data?.dtcHistory || []} initialSearch={initialSearch} />
        )}

        {loaded && data && (data?.dtcHistory?.length || 0) === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container>
  );
}

